import {
  COLOR_BLACK,
  COLOR_BLUE_700,
  COLOR_CHART_400,
  COLOR_CHART_700,
  COLOR_GREEN_700,
  COLOR_GREY_300,
  COLOR_GREY_500,
  COLOR_GREY_550,
  COLOR_GREY_600,
  COLOR_WHITE,
  FAMILY_SANS,
} from '@eleveo/foundation';
import { Options as HighchartsOptions, PlotLineLabelOptions } from 'highcharts';

import { ChartDataSeries, SlaChartDataSeries, StaffingChartDataSeries } from '../../graphql/components';
import { MIN_1, MIN_15 } from '../Scheduling/config';
import { ChartsTypes, CommonChartConfig } from './types';

export const DEFAULT_FONT_SIZE = '11px';
export const DEFAULT_FONT_COLOR = COLOR_GREY_600;

export const DEFAULT_PERCENTAGE_LOCALE_OPTIONS: Intl.NumberFormatOptions = {
  style: 'percent',
};

export const intradayChartsTypesOrdered: Array<keyof ChartsTypes> = [
  'contactVolume',
  'aht',
  'staffingRequirement',
  'sla',
];

export const emptyChartsMetadata = {
  aht: [],
  contactVolume: [],
  sla: [],
  staffingRequirement: [],
};

export const contactVolumeConfig: CommonChartConfig<ChartDataSeries> = {
  colorTag: 'blue-700',
  id: 'contact-volume',
  nameKey: 'contactVolumes',
  seriesConfig: [
    {
      color: COLOR_BLUE_700,
      isDashed: false,
      nameKey: 'current' as const,
    },
    {
      color: COLOR_BLUE_700,
      isDashed: true,
      nameKey: 'trend' as const,
    },
    {
      color: COLOR_GREY_500,
      isDashed: false,
      nameKey: 'forecasted' as const,
    },
  ],
  seriesTrendLegendRelatedSeries: 'current',
  shouldRenderExtraSeriesLegendRow: true,
};

export const ahtConfig: CommonChartConfig<ChartDataSeries> = {
  colorTag: 'chart-400',
  id: 'aht',
  nameKey: 'averageHandleTime',
  seriesConfig: [
    {
      color: COLOR_CHART_400,
      isDashed: false,
      nameKey: 'current' as const,
    },
    {
      color: COLOR_CHART_400,
      isDashed: true,
      nameKey: 'trend' as const,
    },
    {
      color: COLOR_GREY_500,
      isDashed: false,
      nameKey: 'forecasted' as const,
    },
  ],
  seriesTrendLegendRelatedSeries: 'current',
  shouldRenderExtraSeriesLegendRow: true,
};

export const staffingRequirementConfig: CommonChartConfig<StaffingChartDataSeries> = {
  colorTag: 'green-700',
  id: 'staffing-requirement',
  nameKey: 'staffingRequirements',
  seriesConfig: [
    {
      color: COLOR_BLUE_700,
      isDashed: false,
      nameKey: 'required' as const,
    },
    {
      color: COLOR_BLUE_700,
      isDashed: true,
      nameKey: 'trend' as const,
    },
    {
      color: COLOR_GREY_500,
      isDashed: false,
      nameKey: 'forecasted' as const,
    },
    {
      color: COLOR_GREEN_700,
      isDashed: false,
      nameKey: 'scheduled' as const,
    },
  ],
  seriesTrendLegendRelatedSeries: 'required',
};

export const slaConfig: CommonChartConfig<SlaChartDataSeries> = {
  colorTag: 'chart-700',
  id: 'sla',
  nameKey: 'serviceLevelAgreement',
  seriesConfig: [
    {
      color: COLOR_CHART_700,
      isDashed: false,
      nameKey: 'current' as const,
    },
    {
      color: COLOR_CHART_700,
      isDashed: true,
      nameKey: 'trend' as const,
    },
    {
      color: COLOR_GREY_550,
      isDashed: false,
      nameKey: 'benchmark' as const,
    },
  ],
  shouldRenderExtraSeriesLegendRow: true,
  seriesTrendLegendRelatedSeries: 'current',
  isPercentage: true,
  yAxisConfig: {
    softMin: 0,
    softMax: 1,
    tickPositions: [0, 0.5, 1],
  },
};

export const chartsConfig = {
  aht: ahtConfig,
  contactVolume: contactVolumeConfig,
  sla: slaConfig,
  staffingRequirement: staffingRequirementConfig,
} as const;

export const staticChartOptions: HighchartsOptions = {
  boost: {
    enabled: true,
    useGPUTranslations: true,
  },
  credits: {
    enabled: false,
  },
  chart: {
    spacing: [35, 5, 0, 15],
    style: {
      fontFamily: FAMILY_SANS,
    },
    zoomType: 'x',
  },
  legend: {
    enabled: false,
  },
  plotOptions: {
    series: {
      marker: {
        enabled: false,
        fillColor: COLOR_WHITE,
        lineWidth: 3,
        radius: 2,
        symbol: 'circle',
        states: {
          hover: {
            enabled: true,
          },
        },
      },
      states: {
        inactive: {
          opacity: 1,
        },
        hover: {
          lineWidthPlus: 0,
          halo: {
            opacity: 0,
          },
        },
      },
    },
  },
  series: [
    {
      events: {
        mouseOver: () => false,
        mouseOut: () => false,
      },
      lineWidth: 2,
      type: 'line',
    },
  ],
  title: {
    text: '',
  },
  xAxis: {
    crosshair: true,
    labels: {
      align: 'center',
      autoRotation: false,
      autoRotationLimit: 50,
      overflow: 'justify',
      style: {
        color: COLOR_GREY_600,
        fontSize: DEFAULT_FONT_SIZE,
      },
      y: 30,
    },
    lineColor: COLOR_GREY_300,
    minRange: MIN_15,
    minPadding: 0,
    maxPadding: 0,
    showEmpty: false,
    minTickInterval: MIN_1,
    tickWidth: 0,
    title: {
      text: '',
    },
    type: 'datetime',
    plotLines: [
      {
        className: 'intraday__chart__data-timestamp-indicator',
        width: 1,
        color: COLOR_BLACK,
        label: {
          align: 'center',
          className: 'intraday__chart__data-timestamp-indicator-label',
          x: -1,
          rotation: 0,
          text: '',
          useHTML: true,
          textAlign: 'center',
          verticalAlign: 'top',
          y: 0,
        } as PlotLineLabelOptions,
        id: 'data-timestamp-indicator',
      },
    ],
  },
  tooltip: {
    borderColor: 'transparent',
    borderWidth: 0,
    distance: 0,
    hideDelay: 50,
    outside: true,
    padding: 0,
    shadow: false,
    shape: 'square',
    shared: true,
    useHTML: true,
  },
  yAxis: {
    endOnTick: true,
    labels: {
      style: {
        color: COLOR_GREY_600,
        fontSize: DEFAULT_FONT_SIZE,
        zIndex: '0',
      },
      y: -5,
      zIndex: 0,
    },
    showEmpty: false,
    tickAmount: 3,
    title: {
      text: '',
    },
  },
};
